import React, { Component, useEffect } from "react";
import MetaTags from 'react-meta-tags';
import { Formik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from 'react-redux'
import withSizes from "react-sizes";

import { Header, Input, Button } from "../../components";
import Turnstile from "../../components/Turnstile/Turnstile";

import style from "./Contact.module.scss";
import {
  loadContactPageData,
  sendContactUsData,
} from '../../store/slices/contact'
import useLocale from "../../hooks/useLocale";
import { mapSeo, normalizeMedia } from "../../api/strapi";

function HookWrapper({ render }) {
  const locale = useLocale();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(loadContactPageData(locale));
  }, [dispatch, locale]);

  return render(dispatch, loadGlossary(), loadSeo(), loadBanner(), locale);
}

function loadGlossary() {
  const { data } = useSelector(state => state.global)
  return (data && data.glossary) || {}
}

function loadSeo() {
  const { data } = useSelector(state => state.contact)
  return mapSeo(data && data.seo)
}

function loadBanner() {
  const { data } = useSelector(state => state.contact)
  const banner = data && data.banner
  return {
    title: (banner && banner.bannerTitle) || null,
    caption: (banner && banner.bannerDescription) || null,
    image: (normalizeMedia(banner && banner.bannerImage) || {}).file_url || "/assets/service-interior.png",
  }
}

class ContactUs extends Component {
  constructor(props) {
    super(props);
    this.state = { success: false, fail: false, sent: false, token: '' };
    this.turnstileRef = React.createRef();
    this.renderSuccess = this.renderSuccess.bind(this);
    this.renderFailed = this.renderFailed.bind(this);
    this.retry = this.retry.bind(this);
  }

  onTurnstileVerify = (token) => {
    this.setState({ token })
  }

  onTurnstileExpire = () => {
    this.setState({ token: '' })
  }

  renderSuccess = (isMobile, glossary, locale) => (
    <div className={style.status__container}>
      <div className={style.status__title}>{glossary.success||'Success'}!</div>
      <p className={style.status__content}>{glossary.yourinquiry||'Your inquiry has been successfully sent to us. Give us a little time to sort it out and get back to you. Thank you!'}</p>
      <Button full={isMobile} type="redirect" link={`/${locale}`} buttonName={glossary.gohome||'Go to Home'} />
    </div>
  )

  renderFailed = (isMobile) => (
    <div className={style.status__container}>
      <div className={style.status__title}>Whoops!!</div>
      <p className={style.status__content}>It seems there is some error in sending the inquiry. You can try to contact us via phone at <span className={style.status__number}>+62 (021) 94948869</span>.
      <br /><br />If you want to resubmit the form, we will take you back.</p>
      <Button full={isMobile} buttonName="Retry Form" onClick={this.retry} />
    </div>
  )

  async send(payload, dispatch) {
    try {
      await dispatch(sendContactUsData(payload))
      this.setState({ success: true, fail: false, sent: true, token: '' })
    } catch (e) {
      this.setState({ success: false, fail: true, sent: true, token: '' })
    }
  }

  retry(){
    this.setState({
      success: false,
      fail: false,
      sent: false,
      token: ''
    }, () => {
      if (this.turnstileRef.current && this.turnstileRef.current.reset) {
        this.turnstileRef.current.reset()
      }
    })
  }

  render() {
    const { isMobile } = this.props;
    const { token } = this.state;
    const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

    return (
      <HookWrapper render={(dispatch, glossary, seo, banner, locale) =>
      <>
        <MetaTags>
          <title>{seo.title}</title>
          <meta name="description" content={seo.meta_description} />
          <meta name="keyword" content={seo.meta_keyword} />
          <meta property="og:title" content={seo.title} />
        </MetaTags>
        <Header />
        <div className={`page__container ${style[`contact__page--container`]}`}>
          <div className={style.container}>
            <div className={`${style[`contact__container--image`]}`}>
              <img src={banner.image} className={style.contact__image} alt="" />
            </div>
            <div className={style.container__form}>
              <div>
                <div className={style.form__header}>
                  <h3 className={style.contact__title}>
                    {banner.title || glossary.beginWork || 'Let the Work Begin!'}
                  </h3>
                  <p className={style.contact__caption}>
                  {banner.caption || (
                    <>
                      {glossary.whatever || 'Whatever the challenge, AURA Design is up to it.'}
                      <br />
                      {glossary.wehappy || "We're happy to discuss your project and answer any question:"}
                    </>
                  )}
                  </p>
                </div>
                <div className={style.form__wrapper}>
                  {!this.state.sent && <Formik
                      initialValues={{
                        username: '',
                        subject: '',
                        email: '',
                        message: '',
                      }}
                      validationSchema={yup.object().shape({
                        username: yup
                          .string()
                          .required((glossary.name||'Name')+' '+(glossary.mustFilled || 'must be filled')),
                        subject: yup
                          .string()
                          .required((glossary.subject||'Subject')+' '+(glossary.mustFilled || 'must be filled')),
                        email: yup
                          .string()
                          .email(glossary.wrongFormat||'Wrong e-mail format')
                          .required((glossary.email||'Email')+' '+(glossary.mustFilled || 'must be filled')),
                        message: yup
                          .string()
                          .required(glossary.cantempty||'Message cannot be empty'),
                      })}
                      onSubmit={(values, actions) => {
                         if (!token) {
                           actions.setSubmitting(false);
                           return;
                         }
                         this.send({
                           name: values.username,
                           phone: '',
                           email: values.email,
                           subject: values.subject,
                           message: values.message,
                           token: token,
                           locale,
                         }, dispatch)
                        setTimeout(() => {
                          actions.setSubmitting(false);
                        }, 1000);
                      }}
                    >
                      {props => (
                        <form onSubmit={props.handleSubmit}>
                          <Input
                          full
                          type="text"
                          id = "username"
                          value ={props.values.username}
                          placeholder ={glossary.name ||'Name'}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          error = { props.touched.username ? props.errors.username : ''}
                          message = {props.errors.username}
                        />
                        <Input
                          full
                          type="text"
                          id="subject"
                          value={props.values.subject}
                          placeholder={glossary.subject ||'Subject'}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          error = { props.touched.subject ? props.errors.subject : ''}
                          message={props.errors.subject}
                        />
                        <Input
                          full
                          type="email"
                          id="email"
                          value={props.values.email}
                          placeholder={glossary.email ||'E-mail'}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          error = { props.touched.email ? props.errors.email : ''}
                          message={props.errors.email}
                        />
                        <Input
                          full
                          type="textarea"
                          id="message"
                          value={props.values.message}
                          placeholder={glossary.yourMessage ||'Your Message'}
                          onChange={props.handleChange}
                          onBlur={props.handleBlur}
                          error = { props.touched.message ? props.errors.message : ''}
                          message={props.errors.message}
                        />
                        {turnstileSiteKey ? (
                          <div style={{ marginBottom: 16 }}>
                            <Turnstile
                              ref={this.turnstileRef}
                              sitekey={turnstileSiteKey}
                              onVerify={this.onTurnstileVerify}
                              onExpire={this.onTurnstileExpire}
                              onError={this.onTurnstileExpire}
                            />
                          </div>
                        ) : null}
                        <Button full={isMobile} buttonName={glossary.send||'Send'} type="submit" />
                        </form>
                      )}
                    </Formik>}
                </div>
                <>
                 {this.state.fail  &&
                    this.renderFailed(isMobile)
                  }
                  {this.state.success &&
                    this.renderSuccess(isMobile,glossary,locale)
                  }
                  </>
              </div>
            </div>
          </div>
        </div>
      </>
      } />
    )
  }
}

const mapStateToProps = ({ width }) => ({
  isMobile: width < 768,
})

export default withSizes(mapStateToProps)(ContactUs);
